import { useParams } from '@tanstack/react-router'
import canvasConfetti from 'canvas-confetti'
import { Maximize2, Minimize2, Trophy } from 'lucide-react'
import { AnimatePresence, domAnimation, LazyMotion, m } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

import { LIFFQR } from '@/components/common/LIFFQR'
import { useSettingsContext } from '@/contexts/SettingsContext'
import { type RandomNumberWinner } from '@/lib/activities'
import { useDisplayToken } from '@/routes/e/$eventId/_display'

import { useRandomNumberDisplayData } from './useRandomNumberDisplayData'

export function RandomNumberDisplayPage() {
  const { eventId } = useParams({
    from: '/e/$eventId/_display/random-number',
  })
  const { settings } = useSettingsContext()
  const coupleNames = [
    (settings as any)?.brideName,
    (settings as any)?.groomName,
  ]
    .filter(Boolean)
    .join(' & ')
  const token = useDisplayToken()
  const { state } = useRandomNumberDisplayData(eventId, token)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [particles, setParticles] = useState<
    Array<{
      id: number
      left: number
      top: number
      size: number
      delay: number
      duration: number
    }>
  >([])

  // New state variables for slot-machine spin and suspense reveal delay
  const [displayedWinner, setDisplayedWinner] =
    useState<RandomNumberWinner | null>(null)
  const [displayedNumber, setDisplayedNumber] = useState<string | number>('???')
  const [isSpinning, setIsSpinning] = useState(false)
  const [showWinnerDetails, setShowWinnerDetails] = useState(false)

  // Generate stable random rose/blush particles once on mount
  useEffect(() => {
    const items = Array.from({ length: 40 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 3 + 2, // 2px to 5px
      delay: Math.random() * -30, // Negative delay to start animation immediately
      duration: Math.random() * 20 + 15, // 15s to 35s
    }))
    setParticles(items)
  }, [])

  const handleWin = (num: number) => {
    const duration = 12 * 1000
    const animationEnd = Date.now() + duration
    const defaults = {
      startVelocity: 45,
      spread: 360,
      ticks: 120,
      zIndex: 30,
      colors: ['#d8959b', '#f2d1d4', '#ffffff', '#FFD700'],
    }

    const randomInRange = (min: number, max: number) =>
      Math.random() * (max - min) + min

    const interval: any = setInterval(function () {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 80 * (timeLeft / duration)
      canvasConfetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      })
      canvasConfetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      })
    }, 250)
  }

  // Keep a ref to the latest state to avoid effect re-runs on irrelevant changes (e.g., guest logs/attempts)
  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const serverWinner =
    state && state.winners && state.winners.length > 0 ? state.winners[0] : null
  const serverWinnerId = serverWinner
    ? `${serverWinner.guestId}-${serverWinner.number}`
    : null

  // Effect 1: Keep target number in sync when there's no winner
  useEffect(() => {
    const activeWinner =
      state && state.winners && state.winners.length > 0
        ? state.winners[0]
        : null
    if (!activeWinner && state) {
      setDisplayedNumber(state.targetNumber ?? '???')
    }
  }, [state?.targetNumber, state?.winners])

  // Effect 2: Handle spin and winner reveal animation
  useEffect(() => {
    const currentState = stateRef.current
    if (!currentState) return

    const currentServerWinner =
      currentState && currentState.winners && currentState.winners.length > 0
        ? currentState.winners[0]
        : null

    if (currentServerWinner) {
      setIsSpinning(true)
      setShowWinnerDetails(false)
      setDisplayedWinner(currentServerWinner)

      const min = currentState.settings?.min ?? 1
      const max = currentState.settings?.max ?? 100
      const duration = currentState.settings?.duration ?? 2000

      // Rapidly randomize displayed number
      const interval = setInterval(() => {
        setDisplayedNumber(Math.floor(Math.random() * (max - min + 1)) + min)
      }, 50)

      let revealTimeout: ReturnType<typeof setTimeout> | null = null

      const timeout = setTimeout(() => {
        clearInterval(interval)
        setIsSpinning(false)
        setDisplayedNumber(currentServerWinner.number)

        // 800ms dramatic suspense pause before name plate and confetti reveal
        revealTimeout = setTimeout(() => {
          setShowWinnerDetails(true)
          handleWin(currentServerWinner.number)
        }, 800)
      }, duration)

      return () => {
        clearInterval(interval)
        clearTimeout(timeout)
        if (revealTimeout) clearTimeout(revealTimeout)
      }
    } else {
      // No winner in server state - reset or active draw
      setDisplayedWinner(null)
      setShowWinnerDetails(false)
      setIsSpinning(false)
      setDisplayedNumber(currentState.targetNumber ?? '???')
    }
  }, [serverWinnerId])

  // Monitor browser fullscreen status
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen()
      } else {
        await document.exitFullscreen()
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err)
    }
  }

  if (!state)
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#fbf8f8]">
        <div className="animate-pulse text-2xl font-bold tracking-widest text-[#d8959b] uppercase">
          กำลังเชื่อมต่อระบบ...
        </div>
      </div>
    )

  const isGameActive = state.status === 'active'

  return (
    <LazyMotion features={domAnimation}>
      <div
        style={{
          paddingTop:
            state.settings?.topPadding !== undefined
              ? `${state.settings.topPadding}px`
              : undefined,
        }}
        className="relative flex h-screen w-screen flex-col items-center justify-between overflow-hidden bg-gradient-to-br from-[#fbf8f8] via-[#f9e9ea] to-[#f2d1d4] p-8 select-none"
      >
        {/* Dynamic style block for floating rose sparkles */}
        <style>{`
          @keyframes float-particle {
            0% {
              transform: translateY(105vh) scale(0) rotate(0deg);
              opacity: 0;
            }
            10% {
              opacity: 0.7;
            }
            90% {
              opacity: 0.7;
            }
            100% {
              transform: translateY(-10vh) scale(1) rotate(360deg);
              opacity: 0;
            }
          }
          .rose-particle {
            position: absolute;
            bottom: -20px;
            background: radial-gradient(circle, rgba(216,149,155,0.7) 0%, rgba(242,209,212,0.8) 50%, rgba(216,149,155,0) 70%);
            border-radius: 50%;
            pointer-events: none;
            animation: float-particle var(--duration) linear infinite;
            animation-delay: var(--delay);
            z-index: 1;
          }
        `}</style>

        {/* Ambient glow layers for high dimensional depth */}
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          {/* Radial glow in center */}
          <div className="absolute top-[30%] left-[25%] size-[50%] rounded-full bg-[#d8959b]/10 blur-[120px]" />
          <div className="absolute top-[20%] right-[20%] size-[40%] rounded-full bg-[#f2d1d4]/20 blur-[100px]" />
          {/* Soft background light */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(216,149,155,0.15)_0%,transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(216,149,155,0.1)_0%,transparent_50%)]" />
        </div>

        {/* Stable particles animated upwards */}
        <div className="pointer-events-none absolute inset-0 z-1 overflow-hidden">
          {particles.map((p) => (
            <div
              key={p.id}
              className="rose-particle"
              style={{
                left: `${p.left}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                // @ts-expect-error unknown key
                '--delay': `${p.delay}s`,
                '--duration': `${p.duration}s`,
              }}
            />
          ))}
        </div>

        {/* Screen control Overlay (Fullscreen Button) */}
        <button
          onClick={toggleFullscreen}
          className="absolute top-6 right-6 z-50 flex items-center justify-center rounded-full border border-[#d8959b]/35 bg-white/60 p-3.5 text-[#d8959b] transition-all outline-none hover:border-[#d8959b]/50 hover:bg-white/80 hover:text-[#d8959b] active:scale-95"
          title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        >
          {isFullscreen ? (
            <Minimize2 className="size-5" />
          ) : (
            <Maximize2 className="size-5" />
          )}
        </button>

        {/* Header - Couples Name & Title */}
        <div className="z-10 pt-4 text-center">
          <m.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <span className="text-sm font-bold tracking-[0.4em] text-[#d8959b] uppercase md:text-base">
              {coupleNames || 'WEDDING'}
            </span>
            <m.h1
              animate={
                (isGameActive || isSpinning) && !showWinnerDetails
                  ? { scale: [1, 1.02, 1] }
                  : {}
              }
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              className="text-4xl font-extrabold tracking-wider text-[#5c2d31] uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)] md:text-5xl"
            >
              {showWinnerDetails
                ? 'LUCKY DRAW WINNER'
                : isGameActive || isSpinning
                  ? 'ARE YOU THE LUCKY ONE?'
                  : 'LUCKY DRAW ACTIVITY'}
            </m.h1>
            <div className="mx-auto h-[2px] w-36 bg-gradient-to-r from-transparent via-[#d8959b]/35 to-transparent" />
          </m.div>
        </div>

        {/* Main Lucky Number Area */}
        <div className="z-10 flex w-full flex-1 items-center justify-center py-4">
          <div className="flex w-full max-w-6xl flex-col items-center justify-center gap-12 px-6 lg:flex-row">
            <div className="flex flex-col items-center justify-center">
              {displayedWinner && showWinnerDetails ? (
                <AnimatePresence mode="wait">
                  <m.div
                    key="winner"
                    initial={{ y: 35, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    className="flex max-w-4xl flex-col items-center gap-4.5"
                  >
                    {/* Winner Badge Announcement */}
                    <div className="flex h-[44px] items-center justify-center">
                      <AnimatePresence>
                        {showWinnerDetails && (
                          <m.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{
                              type: 'spring',
                              stiffness: 200,
                              damping: 15,
                            }}
                            className="flex items-center gap-2 rounded-full border border-[#d8959b]/45 bg-gradient-to-r from-[#d8959b]/5 via-[#d8959b]/10 to-[#d8959b]/5 px-6 py-2 shadow-[0_6px_20px_rgba(216,149,155,0.1)]"
                          >
                            <Trophy className="size-5 animate-bounce text-[#d8959b] md:size-6" />
                            <span className="text-lg font-bold tracking-wider text-[#5c2d31] md:text-xl">
                              ขอแสดงความยินดีกับผู้โชคดี!
                            </span>
                            <Trophy className="size-5 animate-bounce text-[#d8959b] md:size-6" />
                          </m.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Framed winner number */}
                    <m.div
                      initial={{ rotate: -2, scale: 0.95 }}
                      animate={{ rotate: 0, scale: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 90,
                        damping: 12,
                      }}
                      className="relative flex min-w-[240px] items-center justify-center rounded-[3rem] border-[3px] border-[#d8959b]/40 bg-white/95 px-16 py-4 shadow-[0_15px_40px_rgba(216,149,155,0.2)] md:min-w-[420px]"
                    >
                      {/* Outer glowing halo */}
                      <div className="absolute inset-[-4px] rounded-[3.2rem] border border-[#d8959b]/30 opacity-50 blur-xs" />
                      {/* Inner dashed ring */}
                      <div className="absolute inset-2.5 rounded-[2.8rem] border-2 border-dashed border-[#d8959b]/15" />

                      <span className="bg-gradient-to-b from-[#5c2d31] via-[#d8959b] to-[#a35e64] bg-clip-text text-[clamp(6rem,22vh,14rem)] leading-none font-bold text-transparent tabular-nums drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)] select-none">
                        {displayedNumber}
                      </span>
                    </m.div>

                    {/* Winner Name Plate */}
                    <div className="flex h-[96px] items-center justify-center">
                      <AnimatePresence>
                        {showWinnerDetails && (
                          <m.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 20, opacity: 0 }}
                            transition={{
                              type: 'spring',
                              stiffness: 150,
                              damping: 15,
                            }}
                            className="flex flex-col items-center gap-2"
                          >
                            <div className="rounded-[24px] bg-gradient-to-b from-[#f2d1d4] via-[#d8959b] to-[#c78288] p-[2px] shadow-[0_10px_30px_rgba(216,149,155,0.3)]">
                              <div className="min-w-[300px] rounded-[22px] border border-[#d8959b]/15 bg-[#d8959b] px-14 py-3.5 text-center md:min-w-[500px]">
                                <span className="text-[clamp(1.6rem,4.5vh,3rem)] font-bold tracking-wide text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]">
                                  {displayedWinner.name}
                                </span>
                              </div>
                            </div>
                          </m.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </m.div>
                </AnimatePresence>
              ) : (
                <div className="flex flex-col items-center gap-5">
                  <div className="rounded-full border border-[#d8959b]/35 bg-white/70 px-6 py-2 text-base font-bold tracking-[0.2em] text-[#d8959b] uppercase shadow-[0_5px_20px_rgba(216,149,155,0.1)] backdrop-blur-md md:text-lg">
                    {isGameActive || isSpinning
                      ? 'เลขนำโชคเป้าหมาย'
                      : 'กิจกรรมสุ่มเลขนำโชค'}
                  </div>

                  {/* Magnificent glass number container */}
                  <m.div
                    animate={
                      isGameActive || isSpinning
                        ? {
                            scale: [1, 1.015, 1],
                            boxShadow: [
                              '0 8px 30px rgba(216,149,155,0.1)',
                              '0 15px 40px rgba(216,149,155,0.2)',
                              '0 8px 30px rgba(216,149,155,0.1)',
                            ],
                          }
                        : {}
                    }
                    transition={{
                      repeat: Infinity,
                      duration: 4,
                      ease: 'easeInOut',
                    }}
                    className="relative flex min-w-[280px] items-center justify-center rounded-[3rem] border-2 border-[#d8959b]/25 bg-white/90 px-16 py-6 shadow-[0_15px_40px_rgba(216,149,155,0.12)] backdrop-blur-xl transition-all md:min-w-[450px]"
                  >
                    {/* Decorative classic rose-dark corners */}
                    <div className="absolute top-3 left-3 size-5 rounded-tl-md border-t-2 border-l-2 border-[#d8959b]/40" />
                    <div className="absolute top-3 right-3 size-5 rounded-tr-md border-t-2 border-r-2 border-[#d8959b]/40" />
                    <div className="absolute bottom-3 left-3 size-5 rounded-bl-md border-b-2 border-l-2 border-[#d8959b]/40" />
                    <div className="absolute right-3 bottom-3 size-5 rounded-br-md border-r-2 border-b-2 border-[#d8959b]/40" />

                    <span className="bg-gradient-to-b from-[#5c2d31] via-[#d8959b] to-[#a35e64] bg-clip-text text-[clamp(6rem,22vh,14rem)] leading-none font-bold text-transparent tabular-nums drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)] select-none">
                      {displayedNumber}
                    </span>
                  </m.div>

                  {/* Game status tag */}
                  <div className="rounded-xl border border-[#d8959b]/15 bg-[#d8959b]/10 px-6 py-2.5 shadow-xl backdrop-blur-md">
                    <p className="text-center text-base font-bold tracking-wide text-[#5c2d31] md:text-lg">
                      {isGameActive || isSpinning
                        ? `ช่วงตัวเลขสุ่ม: ${state.settings.min} - ${state.settings.max}`
                        : 'รอกิจกรรมเริ่มต้นจากผู้ดูแลระบบ...'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* QR Code card display */}
            <AnimatePresence>
              {state.showQRCode !== false && (
                <m.div
                  initial={{ opacity: 0, x: 50, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 50, scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 100, damping: 15 }}
                  className="relative flex shrink-0 flex-col items-center gap-4 rounded-[2.5rem] border-2 border-[#d8959b]/25 bg-white/90 p-8 shadow-[0_15px_40px_rgba(216,149,155,0.12)] backdrop-blur-xl"
                >
                  {/* Decorative classic rose-dark corners */}
                  <div className="absolute top-3 left-3 size-4 rounded-tl-md border-t-2 border-l-2 border-[#d8959b]/40" />
                  <div className="absolute top-3 right-3 size-4 rounded-tr-md border-t-2 border-r-2 border-[#d8959b]/40" />
                  <div className="absolute bottom-3 left-3 size-4 rounded-bl-md border-b-2 border-l-2 border-[#d8959b]/40" />
                  <div className="absolute right-3 bottom-3 size-4 rounded-br-md border-r-2 border-b-2 border-[#d8959b]/40" />

                  <div className="text-center">
                    <h3 className="text-lg font-extrabold tracking-wider text-[#5c2d31] uppercase">
                      Scan to Join
                    </h3>
                    <p className="mt-0.5 text-xs font-semibold text-[#d8959b]">
                      สแกนเพื่อร่วมสนุกเล่นเกม
                    </p>
                  </div>

                  <div className="relative rounded-2xl border border-[#d8959b]/15 bg-white p-4 shadow-xs">
                    <LIFFQR
                      path={`/e/${eventId}/_dashboard/activities/random-number`}
                      size={180}
                      color="#5c2d31"
                    />
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer Branding - Beautiful minimal classic design */}
        <div className="pointer-events-none z-10 flex flex-col items-center gap-1 pb-4 text-center">
          <div className="mb-1.5 h-[1px] w-24 bg-gradient-to-r from-transparent via-[#d8959b]/25 to-transparent" />
          <p className="text-sm font-bold tracking-[0.3em] text-[#d8959b] uppercase md:text-base">
            {coupleNames || 'WEDDING'}
          </p>
          <p className="text-[10px] font-semibold tracking-[0.45em] text-[#d8959b]/60 uppercase md:text-xs">
            Wedding Lucky Draw
          </p>
        </div>
      </div>
    </LazyMotion>
  )
}
